'use client';

import React from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import {
  useFestivePlanItems,
  type FestiveCardItem,
} from '../useFestivePlanItems';
import { PLCTA } from 'components/performance-lab/PLCTA';
import { useOpenCallbackModal } from '../../hooks';
import {
  getPerformanceLabBuyHref,
  type PerformanceLabBuyCategory,
} from '../../utils';
import {
  festivePlanGroupAnchorId,
  type FestivePanelKey,
} from '../festiveOffer';
import { FESTIVE_CDN_ASSETS } from '../festiveAssets';
import { useRevealOnce } from './useFestiveScrollMotion';
import { FestiveGiftCard } from './FestiveGiftCard';
import styles from './FestivePlanPanelsV2.module.css';

/** Desktop and mobile crops, exported separately so neither breakpoint is repositioned. */
const PANEL_PHOTO: Record<FestivePanelKey, { desk: string; mobile: string }> = {
  // Swapped on the gift page only: the pair leads Diagnostics, the woman Memberships.
  diagnostics: {
    desk: FESTIVE_CDN_ASSETS.membershipBannerDesk,
    mobile: FESTIVE_CDN_ASSETS.membershipBannerMobile,
  },
  membership: {
    desk: FESTIVE_CDN_ASSETS.diagnosticsBannerDesk,
    mobile: FESTIVE_CDN_ASSETS.diagnosticsBannerMobile,
  },
  recovery: {
    desk: FESTIVE_CDN_ASSETS.recoveryBannerDesk,
    mobile: FESTIVE_CDN_ASSETS.recoveryBannerMobile,
  },
  iv: {
    desk: FESTIVE_CDN_ASSETS.ivBannerDesk,
    mobile: FESTIVE_CDN_ASSETS.ivBannerMobile,
  },
};

export type PlanPanelRow = {
  /** `thumb3`: three thumbnail cards. `wide2`: two wide cards. `feature`: one full-width card. */
  kind: 'thumb3' | 'wide2' | 'feature';
  items: FestiveCardItem[];
};

/**
 * One gradient plan panel. `look` picks the art direction: `longevity` = woman cut-out on
 * the left, copy on the right, sky → sand; `recovery` = man on the right, copy left, stone.
 * `type="geist"` is the V2 type treatment (Geist, no Dharma) and drops the header CTAs.
 */
export const PlanPanel: React.FC<{
  look: 'longevity' | 'recovery';
  /** Which staged banner pair to show. Separate from `look`, which only sets the gradient. */
  photoKey: FestivePanelKey;
  headingKey: string;
  bodyKey: string;
  buyCategory: PerformanceLabBuyCategory;
  rows: PlanPanelRow[];
  id?: string;
  type?: 'geist';
  /** V2 (Figma 5340:3341): each panel's own gradient, white copy on top. */
  tone?: 'sky' | 'stone' | 'lavender' | 'clay';
  tall?: boolean;
}> = ({
  look,
  photoKey,
  headingKey,
  bodyKey,
  buyCategory,
  rows,
  id,
  type,
  tone,
  tall,
}) => {
  const t = useTranslations('performance-lab');
  const openCallback = useOpenCallbackModal();
  const headerRef = useRevealOnce<HTMLDivElement>();
  const cardsRef = useRevealOnce<HTMLDivElement>('0px 0px -8% 0px');
  let slot = 0;

  return (
    <section
      id={id}
      className={styles.panel}
      data-panel={look}
      data-banner={photoKey}
      data-type={type}
      data-tone={tone}
      data-has-feature={tall ? 'true' : undefined}
      aria-label={t(headingKey)}
    >
      <div className={styles.intro}>
        <div className={styles.photo} data-banner={photoKey} aria-hidden="true">
          <div className={styles.photoBox}>
            <CustomImage
              className={styles.photoDesktop}
              src={PANEL_PHOTO[photoKey].desk}
              alt=""
              fill
              sizes="(max-width: 991px) 100vw, 1100px"
            />
            <CustomImage
              className={styles.photoMobile}
              src={PANEL_PHOTO[photoKey].mobile}
              alt=""
              fill
              sizes="200vw"
            />
          </div>
        </div>

        <div className={styles.headerContainer}>
          <div
            ref={headerRef}
            className={`${styles.header} ${styles.reveal}`}
            data-align={look === 'longevity' ? 'right' : 'left'}
          >
            {type === 'geist' ? (
              <p className={styles.offerBadge}>{t('festive.lineup.save15')}</p>
            ) : null}
            {/* Wraps only where the column runs out; a `\n` in the copy collapses to a space. */}
            <h2 className={styles.heading}>{t(headingKey)}</h2>
            <div className={styles.body}>
              {t(bodyKey)
                .split('\n\n')
                .map((paragraph, index) => (
                  <p key={index} className={styles.paragraph}>
                    {paragraph}
                  </p>
                ))}
            </div>
            {type !== 'geist' && (
              <div className={styles.actions}>
                <PLCTA
                  type="button"
                  className={`${styles.primary} pl-custom-type`}
                  onClick={() => openCallback({ flow: 'pl_festive_callback' })}
                  data-buttontype={`performance_lab_festive_${look}_callback`}
                >
                  {t('festive.versions.hero.cta')}
                </PLCTA>
                <CustomLink
                  className={styles.outline}
                  href={getPerformanceLabBuyHref({ category: buyCategory })}
                  data-buttontype={`performance_lab_festive_${look}_explore`}
                >
                  {t('festive.versions.plans.explore')}
                </CustomLink>
              </div>
            )}
          </div>
        </div>
      </div>

      <div ref={cardsRef} className={styles.cards}>
        {rows.map((row) => {
          if (!row.items.length) return null;
          if (row.kind === 'feature') {
            return row.items.map((item) => (
              <div
                className={styles.cardSlot}
                key={item.id}
                style={{ '--i': slot++ } as React.CSSProperties}
              >
                <FestiveGiftCard item={item} variant="feature" type={type} />
              </div>
            ));
          }
          return (
            <div
              className={styles.row}
              data-columns={row.kind === 'wide2' ? '2' : '3'}
              key={row.items[0].id}
            >
              {row.items.map((item) => (
                <div
                  className={styles.cardSlot}
                  key={item.id}
                  style={{ '--i': slot++ } as React.CSSProperties}
                >
                  <FestiveGiftCard
                    item={item}
                    variant="thumb"
                    wide={row.kind === 'wide2'}
                    type={type}
                  />
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </section>
  );
};

/** Gift panels: diagnostics, memberships, recovery groups, and IV. */
export const FestivePlanPanelsGeist: React.FC = () => {
  const panels = useFestivePlanItems();

  return (
    <>
      {panels.map((panel) => {
        const rows: PlanPanelRow[] = [];
        for (
          let index = 0;
          index < panel.cards.length;
          index += panel.def.columns
        ) {
          const items = panel.cards.slice(index, index + panel.def.columns);
          rows.push({
            kind: panel.def.columns === 2 ? 'wide2' : 'thumb3',
            items,
          });
        }
        return (
          <div
            className={styles.group}
            id={festivePlanGroupAnchorId(panel.def.key)}
            key={panel.def.key}
          >
            <PlanPanel
              type="geist"
              look={panel.def.look}
              photoKey={panel.def.key}
              tone={panel.def.tone}
              headingKey={panel.def.headingKey}
              bodyKey={panel.def.bodyKey}
              buyCategory={
                panel.def.key === 'diagnostics' ? 'diagnostics' : 'recovery'
              }
              rows={rows}
            />
          </div>
        );
      })}
    </>
  );
};
